/**
 * The `communities` module: the Parcels map, the ARB map and the builders
 * of the current community. Its server half is app/resources/plugins/arc/
 * (sites, maps, features, builders, forms, files).
 *
 * Every page is site-scoped (#/s/<slug>/…); the shell rewrites `:site` for
 * the community in the switcher and shows an item only when the person
 * holds its `sitePerm` there.
 */

const seg = encodeURIComponent;

export default {
  // The module's pages load on the first route that needs them (core/js/modules.js);
  // the shell warms them in idle time after sign-in. Boot carries the shell only.
  load: () => Promise.all([import('../components/site-home.js'), import('../components/communities-page.js'), import('../components/map-page.js'), import('../components/builders.js')]),
  name: 'communities',
  home: '/home',

  nav: {
    area: 'Community',
    items: [
      { route: 's/:site/home',     label: 'Home' },
      { route: 's/:site/parcels',  label: 'Parcels',  sitePerm: 'ParcelManage' },
      { route: 's/:site/arb',      label: 'ARB',      sitePerm: 'ARBView' },
      { route: 's/:site/builders', label: 'Builders', sitePerm: 'ParcelManage' },
    ],
  },

  routes: {
    '/communities':                   'arc-communities',
    '/s/:site/home':                  'arc-site-home',
    '/s/:site/parcels':               'arc-map-page',
    '/s/:site/arb':                   'arc-map-page',
    '/s/:site/builders':              'arc-builders',
    '/s/:site/builders/:builderId':   'arc-builder',
    '/s/:site/builders/:builderId/floorplans/:floorplanId': 'arc-floorplan',
  },

  resources: (api) => ({
    maps: {
      // The map as the component loads it: view, tile layers, each layer's presentation.
      config: (siteId, mapName) => api.get(`/sites/${seg(siteId)}/maps/${seg(mapName)}`),
      saveConfig: (siteId, mapName, config) => api.put(`/sites/${seg(siteId)}/maps/${seg(mapName)}`, { config }),
      // The structured subset: { view: {center, resolution} } | { tiles: [...] } | { legend_notes: [...] }.
      save: (siteId, mapName, body) => api.put(`/sites/${seg(siteId)}/maps/${seg(mapName)}`, body),
      layer: (siteId, mapName, layerId) => api.get(`/sites/${seg(siteId)}/maps/${seg(mapName)}/layers/${seg(layerId)}`),
      saveLayer: (siteId, mapName, layerId, body) => api.put(`/sites/${seg(siteId)}/maps/${seg(mapName)}/layers/${seg(layerId)}`, body),
      // Features: the GeoJSON for a viewport and filters (POST, so a long
      // filter list fits); the paged list; one feature; its change.
      features: (layerUri, body) => api.post(`${layerUri}/features`, body),
      search: (layerUri, body) => api.post(`${layerUri}/features/search`, body),
      filters: (layerUri) => api.get(`${layerUri}/filters`),
      feature: (featureUri) => api.get(featureUri),
      saveFeature: (featureUri, body) => api.put(featureUri, body),
      deleteFeature: (featureUri) => api.del(featureUri),
      approve: (featureUri, approved) => api.post(`${featureUri}/approve`, { approved }),
      // Many lots at once: { ids, propdata } or { ids, approved }.
      bulk: (layerUri, body) => api.post(`${layerUri}/features/bulk`, body),
      exportUrl: (layerUri) => `${api.baseUrl}${layerUri}/features/export`,
    },
    forms: {
      get: (siteId, name) => api.get(`/sites/${seg(siteId)}/forms/${seg(name)}`),
      options: (siteId, attribute) => api.get(`/sites/${seg(siteId)}/forms/attributeoptions/${seg(attribute)}`),
    },
    files: {
      list: (siteId, entity, recordId, params) => api.get(`/sites/${seg(siteId)}/files/${seg(entity)}/${seg(recordId)}`, params),
      upload: (siteId, entity, recordId, file, fields) => api.upload(`/sites/${seg(siteId)}/files/${seg(entity)}/${seg(recordId)}`, file, fields),
      sort: (siteId, entity, recordId, ids) => api.post(`/sites/${seg(siteId)}/files/${seg(entity)}/${seg(recordId)}`, { action: 'sort', file_id: ids }),
      update: (fileUri, body) => api.put(fileUri, body),
      remove: (fileUri) => api.del(fileUri),
    },
    builders: {
      list: (siteId, params) => api.get(`/sites/${seg(siteId)}/builders`, params),
      get: (siteId, id) => api.get(`/sites/${seg(siteId)}/builders/${seg(id)}`),
      create: (siteId, body) => api.post(`/sites/${seg(siteId)}/builders`, body),
      update: (siteId, id, body) => api.put(`/sites/${seg(siteId)}/builders/${seg(id)}`, body),
      unlink: (siteId, id) => api.del(`/sites/${seg(siteId)}/builders/${seg(id)}`),
      floorplans: (siteId, builderId) => api.get(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans`),
      createFloorplan: (siteId, builderId, body) => api.post(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans`, body),
      floorplan: (siteId, builderId, id) => api.get(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(id)}`),
      updateFloorplan: (siteId, builderId, id, body) => api.put(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(id)}`, body),
      deleteFloorplan: (siteId, builderId, id) => api.del(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(id)}`),
      createElevation: (siteId, builderId, fpId, body) => api.post(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(fpId)}/elevations`, body),
      updateElevation: (siteId, builderId, fpId, id, body) => api.put(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(fpId)}/elevations/${seg(id)}`, body),
      deleteElevation: (siteId, builderId, fpId, id) => api.del(`/sites/${seg(siteId)}/builders/${seg(builderId)}/floorplans/${seg(fpId)}/elevations/${seg(id)}`),
      allFloorplans: (siteId) => api.get(`/sites/${seg(siteId)}/floorplans`),
    },
  }),
};
